import { useEffect, useMemo } from 'react'

import 'core-js/proposals/promise-with-resolvers'
import { ApolloProvider } from '@apollo/client'
import { ExtraErrorData } from '@sentry/integrations'
import { init, reactRouterV6Instrumentation, setTag, withProfiler } from '@sentry/react'
import { BrowserTracing } from '@sentry/tracing'
import ReactDOM from 'react-dom/client'
import { IntlProvider } from 'react-intl'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'
import { useRegisterSW } from 'virtual:pwa-register/react'

import acreConfig from '@acre/config'
import { Alert, SentryErrorBoundary, theme, ThemeProvider } from '@acre/design-system'
import type { LutherException } from '@acre/graphql'
import { ErrorProvider, flattenMessages, getMessages, getTeamComponentFromPath, Locales } from '@acre/utils'

import '@babel/polyfill'
import './polyfills/pdfjs'

import App from './App'
import initApollo from './bootstrap/client'
import { InstallPromptProvider } from './contexts/InstallPromptContext'
import messages from './intl/messagesClientPortal'

const ClientPortal = () => {
  useRegisterSW()

  const client = useMemo(initApollo, [])
  const flattenedMessages = flattenMessages(getMessages(messages, Locales.GB))

  const isPlaywright = navigator.webdriver
  // @ts-expect-error - Cypress is a global variable
  const isCypress = window.Cypress

  // Disable sentry reporting in test environments
  if (import.meta.env.MODE === 'production' && !isCypress && !isPlaywright) {
    init({
      dsn: 'https://ee7d1ac2845d4d9a998f53da3575f68a@sentry.io/1876005',
      environment: acreConfig.SENTRY_ENVIRONMENT,
      integrations: [
        new BrowserTracing({
          routingInstrumentation: reactRouterV6Instrumentation(
            useEffect,
            useLocation,
            useNavigationType,
            createRoutesFromChildren,
            matchRoutes,
          ),
        }),
        new ExtraErrorData({ depth: 5 }),
      ],
      tracesSampler: (samplingContext) => {
        if (samplingContext?.transactionContext?.op === 'navigation') return 0.015 // 1.5%
        // Apply a 0.15% sample rate for all other transactions (10× lower)
        return 0.0015
      },
      // We want to silence auth 401s as they are not genuine errors - they are the result of the user being logged out
      ignoreErrors: ['Invalid authorisation cookie'],
      beforeSend(event, hint) {
        // This ensures that sentry is grouping our events as we want them, rather than putting all failing API calls together
        const error = hint?.originalException as Error

        // Ignore ApolloErrors
        if (error?.name === 'ApolloError') return null

        const hasGraphqlError =
          event.breadcrumbs?.some(
            (b) => b.category === 'sentry.event' && b.message && /GraphqlError:/i.test(b.message),
          ) || false

        // Skip logging GraphQL errors here, as they're already handled by graphqlErrorLink.
        if (hasGraphqlError) return null

        // Filter out HTTP 401 errors - these are expected auth failures, not application errors
        if (error && typeof error === 'object') {
          // Check for various 401 error patterns
          const is401Error =
            // Direct status code check
            (error as any)?.status === 401 ||
            (error as any)?.statusCode === 401 ||
            // Axios error response
            (error as any)?.response?.status === 401 ||
            // Error message patterns
            error.message?.includes('401')

          if (is401Error) return null
        }

        if (error && typeof error !== 'string' && error.name === 'LutherException') {
          const err = error as LutherException

          event.fingerprint = [err.message]
        }

        // Deducing the team and component from the URL and adding them to the tags list
        const teamTags = getTeamComponentFromPath(window.location.pathname, 'cp')
        event.tags = {
          ...event.tags,
          team: teamTags.team,
          component: teamTags.component,
        }

        return event
      },
    })
    setTag('issue_type', 'frontend')
    setTag('app', 'client-portal')
  }

  // Allows us to use the sentry profiler, can track render times for the app component and descendants
  const AppWithProfiler = withProfiler(App)

  return (
    <ApolloProvider client={client}>
      <SentryErrorBoundary>
        <IntlProvider locale={Locales.GB} messages={flattenedMessages}>
          <ThemeProvider theme={theme}>
            <ErrorProvider alertSlot={Alert}>
              <InstallPromptProvider>
                <AppWithProfiler />
              </InstallPromptProvider>
            </ErrorProvider>
          </ThemeProvider>
        </IntlProvider>
      </SentryErrorBoundary>
    </ApolloProvider>
  )
}

if (!window.Intl) {
  // @ts-ignore
  require.ensure(['intl', 'intl/locale-data/jsonp/en.js'], (require) => {
    require('intl')
    require('intl/locale-data/jsonp/en.js')

    runApp()
  })
} else {
  runApp()
}

function runApp() {
  ReactDOM.createRoot(document.getElementById('app') as HTMLElement).render(<ClientPortal />)
}
